export type PrivacyPreferences = CookieCategoryItem[];

export interface CookieCategoryItem {
  title: string;
  key: 'necessary' | 'analytics';
  description: string;
  cookies: Cookie[];
  status: {
    title: string;
    key: 'alwaysEnabled' | 'enabled' | 'disabled';
  };
}

interface ContentTranslation {
  nameHeader: string;
  durationHeader: string;
  descriptionHeader: string;
  btnAcceptAll: string;
  btnRejectAll: string;
  btnCustomize: string;
  btnSave: string;
  bannerTitle: string;
  bannerText: string;
  preferencesTitle: string;
  preferencesText: string;
}

interface Cookie {
  name: string;
  description: string;
  duration: string;
}

export type SupportedLocaleType = (typeof SupportedLocale)[number];

const SupportedLocale = ['en', 'de'] as const;

export function isSupportedLocale(
  locale: string
): locale is SupportedLocaleType {
  // the type assertion is necessary to satisfy types so `string` can be checked
  // for presence in a `SupportedLocale[]`
  const locales: string[] = SupportedLocale.slice();
  return locales.includes(locale);
}

export const contentTranslations: Record<
  SupportedLocaleType,
  ContentTranslation
> = {
  en: {
    nameHeader: 'Cookie Name',
    durationHeader: 'Cookie Duration',
    descriptionHeader: 'Cookie Description',
    btnAcceptAll: 'Accept All',
    btnRejectAll: 'Reject All',
    btnCustomize: 'Customize',
    btnSave: 'Save and Accept',
    bannerTitle: 'We value your privacy',
    bannerText:
      'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    preferencesTitle: 'Privacy Overview',
    preferencesText:
      'This website uses cookies to improve your experience while you navigate through the website. Out of these, the cookies that are categorized as necessary are stored on your browser as they are essential for the working of basic functionalities of the site.',
  },
  de: {
    nameHeader: 'Cookie-Name',
    durationHeader: 'Cookie-Dauer',
    descriptionHeader: 'Cookie-Beschreibung',
    btnAcceptAll: 'Alle akzeptieren',
    btnRejectAll: 'Alle ablehnen',
    btnCustomize: 'Anpassen',
    btnSave: 'Speichern und Akzeptieren',
    bannerTitle: 'Wir schätzen Ihre Privatsphäre',
    bannerText:
      'Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern, personalisierte Werbung oder Inhalte bereitzustellen und unseren Datenverkehr zu analysieren. Indem Sie auf „Alle akzeptieren“ klicken, stimmen Sie der Verwendung von Cookies zu.',
    preferencesTitle: 'Datenschutzübersicht',
    preferencesText:
      'Diese Website verwendet Cookies, um Ihr Erlebnis beim Navigieren durch die Website zu verbessern. Von diesen werden die als notwendig kategorisierten Cookies in Ihrem Browser gespeichert, da sie für das Funktionieren der Grundfunktionen der Website unerlässlich sind.',
  },
};

export const privacyPreferences: Record<
  SupportedLocaleType,
  PrivacyPreferences
> = {
  en: [
    {
      title: 'Necessary',
      key: 'necessary',
      description:
        'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.',

      cookies: [
        {
          name: 'Session Token',
          description: "Authenticating the user's session with our server",
          duration: '24 hours',
        },
        {
          name: 'DRM Token',
          description:
            'This token is used to authenticate the user with the Widevine DRM service.',
          duration: '24 hours',
        },
        {
          name: 'Chat Token',
          description:
            'This token is used to authenticate the user with the Stream chat service.',
          duration: '24 hours',
        },
      ],
      status: {
        title: 'Always Enabled',
        key: 'alwaysEnabled',
      },
    },
    {
      title: 'Analytics',
      key: 'analytics',
      description:
        'Analytics cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.',
      cookies: [
        {
          name: 'Analytics Token',
          description: 'Logging 1st Party Analytics on site.',
          duration: '3 months',
        },
        {
          name: 'Bitmovin Analytics Token',
          description: 'Logging 3rd Party Video Stream Analytics.',
          duration: '3 months',
        },
      ],
      status: {
        title: 'Enabled',
        key: 'enabled',
      },
    },
  ],
  de: [
    {
      title: 'Notwendig',
      key: 'necessary',
      description:
        'Notwendige Cookies helfen dabei, eine Website nutzbar zu machen, indem sie grundlegende Funktionen wie die Seitennavigation und den Zugriff auf sichere Bereiche der Website ermöglichen. Die Website kann ohne diese Cookies nicht ordnungsgemäß funktionieren.',
      cookies: [
        {
          name: 'Sitzungstoken',
          description:
            'Authentifizierung der Benutzersitzung mit unserem Server',
          duration: '24 Stunden',
        },
        {
          name: 'DRM-Token',
          description:
            'Dieses Token wird verwendet, um den Benutzer mit dem Widevine DRM-Dienst zu authentifizieren.',
          duration: '24 Stunden',
        },
        {
          name: 'Chat-Token',
          description:
            'Dieses Token wird verwendet, um den Benutzer mit dem Stream-Chat-Dienst zu authentifizieren.',
          duration: '24 Stunden',
        },
      ],
      status: {
        title: 'Immer Aktiviert',
        key: 'alwaysEnabled',
      },
    },
    {
      title: 'Analytik',
      key: 'analytics',
      description:
        'Analyse-Cookies helfen Website-Besitzern zu verstehen, wie Besucher mit Websites interagieren, indem sie Informationen anonym sammeln und melden.',
      cookies: [
        {
          name: 'Analytics-Token',
          description:
            'Protokollierung von 1st Party Analytics auf der Website.',
          duration: '3 Monate',
        },
        {
          name: 'Bitmovin Analytics Token',
          description: 'Protokollierung von 3rd Party Video Stream Analytics.',
          duration: '3 Monate',
        },
      ],
      status: {
        title: 'Ermöglicht',
        key: 'enabled',
      },
    },
  ],
};
